import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AdminLayout from '../../../components/AdminLayout';
import ManageClaimCodes from '../../../components/campaign/instant-win/ManageClaimCodes';
import api from '../../../services/api';
import {
  ArrowLeft
} from 'lucide-react';

const ManageClaimCodesPage = () => {
  const navigate = useNavigate();
  const { campaignId, prizeId } = useParams();

  const [prize, setPrize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const fetchPrize = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/campaigns/${campaignId}/instant-win/prizes`);
        const numericPrizeId = Number(prizeId);
        const matchedPrize = data.prizes?.find((item) => Number(item.id) === numericPrizeId) || null;

        if (!matchedPrize) {
          setErrorMessage('Prize not found.');
          setShowError(true);
        }

        setPrize(matchedPrize);
      } catch (error) {
        console.error('Failed to load prize:', error);
        setErrorMessage(error.response?.data?.error || error.message || 'Failed to load prize');
        setShowError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPrize();
  }, [campaignId, prizeId]);

  const handleBack = () => {
    navigate(`/admin/campaigns/${campaignId}/edit?tab=instantwin`);
  };

  const handlePrizeUpdated = (updatedPrize) => {
    if (!updatedPrize) return;
    setPrize((current) => (current ? { ...current, ...updatedPrize } : updatedPrize));
  };

  return (
    <AdminLayout activeSection="campaigns">
      <div className="p-6 md:p-10">
        <div className="flex items-center mb-6">
          <button
            type="button"
            onClick={handleBack}
            className="text-white hover:text-gray-200 mr-4"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-3xl font-bold text-white">Manage Claim Codes</h1>
        </div>

        {showSuccess && (
          <div className="fixed top-4 right-4 z-[120]">
            <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
              <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">{successMessage}</span>
            </div>
          </div>
        )}

        {showError && (
          <div className="fixed top-4 right-4 z-[120]">
            <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
              <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">{errorMessage}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-3xl shadow-2xl py-24 flex items-center justify-center">
            <div className="flex items-center gap-3 text-gray-500">
              <svg
                className="animate-spin h-6 w-6 text-teal-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              <span>Loading prize settings…</span>
            </div>
          </div>
        ) : prize ? (
          <ManageClaimCodes
            variant="page"
            open
            onClose={handleBack}
            campaignId={Number(campaignId)}
            prize={prize}
            api={api}
            onPrizeUpdated={handlePrizeUpdated}
            setSuccessMessage={setSuccessMessage}
            setShowSuccess={setShowSuccess}
            setErrorMessage={setErrorMessage}
            setShowError={setShowError}
          />
        ) : (
          <div className="bg-white rounded-3xl shadow-2xl py-16 text-center text-gray-500">
            Unable to load prize details. Please return and try again.
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManageClaimCodesPage;
